import ChartTooltip from "@ui/ChartTooltip";
import PieChart, { ECOption } from "@ui/PieChart";
import { PieSeriesOption } from "echarts/charts";
import type { TooltipComponentOption } from "echarts/components";
import { TFunction } from "i18next";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";

import EllipseSvg from "@/assets/common/other_icons/ellipse.svg?react";
import { ChartData } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";

function optionFactory(
  data: ChartData,
  extraSeriesParams: PieSeriesOption,
  extraChartParams: ECOption,
  t: TFunction,
  title?: string,
  titleSubText?: string
): ECOption {
  const dataUnit = getCarbonDisplayUnit(data.map((dataItem) => dataItem.value));

  return {
    textStyle: {
      fontFamily: "Poppins",
    },
    title: {
      text: title,
      show: Boolean(title),
      left: "center",
      textVerticalAlign: "top",
      top: "middle",
      subtext: titleSubText,
      itemGap: 5,
      textStyle: {
        fontSize: 20,
        fontWeight: 700,
        color: "#09044A",
      },
      subtextStyle: {
        fontSize: 14,
        fontWeight: 400,
        color: "#09044A",
      },
    },
    tooltip: {
      borderWidth: 0,
      padding: 0,
      extraCssText: "border-radius: 8px;",
      formatter: ({
        name,
        value,
        color,
      }: {
        name: string;
        value: number;
        color: string;
      }) => {
        const { formattedCarbon, unit } = formatCarbon(t, value, dataUnit);
        return renderToString(
          <ChartTooltip
            seriesName={name}
            valueToDisplay={formattedCarbon}
            unit={t(`common:unit.${unit}`)}
            color={color}
            Icon={EllipseSvg}
            iconClassName="size-2"
          />
        );
      },
    } as TooltipComponentOption,
    series: [
      {
        type: "pie",
        radius: ["35%", "85%"],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: "center",
        },
        labelLine: {
          show: false,
        },
        data: data.map((item) => {
          const extraArgs = {} as Record<string, unknown>;

          if (item.color) {
            extraArgs["itemStyle"] = { color: item.color };
          }

          return {
            value: item.value,
            name: item.name,
            ...extraArgs,
          };
        }),
        ...extraSeriesParams,
      },
    ],
    ...extraChartParams,
  };
}

function ConsolidationPie({
  data,
  title,
  titleSubText,
  extraSeriesParams = {},
  extraChartParams = {},
}: {
  data: ChartData;
  title?: string;
  titleSubText?: string;
  extraSeriesParams?: PieSeriesOption;
  extraChartParams?: ECOption;
}) {
  const { t } = useTranslation(["common", "formatter"]);

  const option = optionFactory(
    data,
    extraSeriesParams,
    extraChartParams,
    t,
    title,
    titleSubText
  );

  return <PieChart option={option} />;
}

export default ConsolidationPie;
